import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { Typography } from "@mui/material";
import { AppTable, ExportDialog, ImportExportActions } from "@/components/common";
import { PaginationType } from "@/types/common";
import { ResponseSailorType } from "@/types/service";
import { KeyConstant, LangConstant } from "@/const";
import { SearchDataFilter, useImportRequest, useLoading, useSnackbar } from "@/hooks";
import { CodeSearchFilter, SailorSearchImportDialog } from "./components";
import { MainBodyPage, TableContainer } from "./SailorSearchList.style";
import { CodeSearchFilterValue } from "./hooks/useCodeSearchList";
import useCodeSearchColumn from "./hooks/useCodeSearchColumn";
import useCodeSearchList from "./hooks/useCodeSearchList";
import { isElectron } from "@/utils/common.utils";
import { DEFAULT_PAGINATION } from "@/const/app.const";

const DEFAULT_FILTER: CodeSearchFilterValue = {};

const SailorSearchListPage = () => {
  const { t: getLabel } = useTranslation(LangConstant.NS_REQUEST);
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { openLoading } = useLoading();

  const [pageData, setPageData] = useState<Partial<PaginationType>>({});
  const [filter, setFilter] = useState<CodeSearchFilterValue>(DEFAULT_FILTER);
  const [filesUpload, setFilesUpload] = useState<File[]>([]);
  const [openUpload, setOpenUpload] = useState<boolean>(false);
  const [isOpenExportDialog, setIsOpenExportDialog] = useState<boolean>(false);
  const [selectedRequests, setSelectedRequests] = useState<any[]>([]);

  const { importRequest } = useImportRequest("queryResponse");
  const { columnList } = useCodeSearchColumn();
  const { requestList, pagination } = useCodeSearchList({
    filterParam: filter,
    paginationParam: pageData,
  });

  const onFileUpload = (files: File[]) => {
    setFilesUpload(files);
    setOpenUpload(true);
  };

  const handleSelectedRequest = (uidList: string[]) => {
    setSelectedRequests(requestList.filter((request: ResponseSailorType) => uidList.includes(request.uid)));
  };

  const handleChangeFilter = (filter: SearchDataFilter) => {
    setFilter(filter);
    setPageData(DEFAULT_PAGINATION);
  };

  const handleImportSuccess = () => {
    openLoading(false);
    setOpenUpload(false);
    openSnackbar(getLabel("MSG_UPLOAD_SUCCESS"), "success");
    queryClient.invalidateQueries({
      queryKey: [KeyConstant.REACT_QUERY.getInternalRequest],
    });
  };

  const handleImportError = () => {
    openSnackbar(getLabel("MSG_UPLOAD_FAILURE"), "error");
    openLoading(false);
  };

  const mutationImport = useMutation({
    mutationFn: (file: File) => importRequest(file),
    onSuccess: () => {
      handleImportSuccess();
    },
    onError: () => {
      handleImportError();
    },
  });

  const onImport = () => {
    openLoading(true);
    mutationImport.mutate(filesUpload[0]);
  };

  const handleExport = () => {
    setIsOpenExportDialog(true);
  };

  const handleCloseExportDialog = () => {
    setIsOpenExportDialog(false);
  };

  const handleStartEncrypt = () => {
    openLoading(true);
  };

  const handleEncryptCompleted = async (file: ArrayBuffer) => {
    openLoading(false);
    handleCloseExportDialog();
    const blob = new Blob([file], { type: "application/zip" });
    const aTag = document.createElement("a");
    const url = URL.createObjectURL(blob);
    aTag.href = url;
    aTag.download = "request";
    aTag.click();
    aTag.remove();
  };

  const handleEncryptFailure = () => {
    openLoading(false);
    handleCloseExportDialog();
    openSnackbar(getLabel("MSG_ERROR", { ns: LangConstant.NS_COMMON }), "error");
  };

  return (
    <MainBodyPage>
      <Typography variant="h4" gutterBottom>
        {getLabel("L_SAILOR_SEARCH_TITLE")}
      </Typography>
      <CodeSearchFilter onChangeFilter={handleChangeFilter} filter={filter} />
      {isElectron() && <ImportExportActions onExport={handleExport} onUpload={onFileUpload} />}
      <TableContainer>
        <AppTable
          data={requestList}
          columns={columnList}
          pagination={pagination}
          onChangePagination={setPageData}
          onSelectedRequest={handleSelectedRequest}
        />
      </TableContainer>
      <ExportDialog
        open={isOpenExportDialog}
        mode="queryResponse"
        requests={selectedRequests}
        onClose={handleCloseExportDialog}
        onStart={handleStartEncrypt}
        onSuccess={handleEncryptCompleted}
        onFailure={handleEncryptFailure}
      />
      <SailorSearchImportDialog
        onImport={onImport}
        filesUpload={filesUpload}
        openUpload={openUpload}
        onChangeOpenUpload={setOpenUpload}
      />
    </MainBodyPage>
  );
};
export default SailorSearchListPage;
